<template>
  <BaseDialogForm
    v-model="showCustomerAddressForm"
    :title="
      editMode
        ? `Edit ${customerAddressModule.customerAddress}`
        : `Add ${customerAddressModule.customerAddress}`
    "
    :nonFieldErrors="formErrors.non_field_errors"
    :persistent="true"
  >
    <template #content>
      <v-form
        v-model="isValid"
        ref="customerAddressForm"
        autocomplete="off"
        class="overflow-auto"
        @submit.prevent="submitCustomerAddressForm"
      >
        <v-row class="ma-0" v-if="!customerParentObj && !editMode">
          <v-col cols="12" md="6" lg="6">
            <SelectField
              id="customer"
              :label="`${customerModule.customer}*`"
              :itemsList="customerList"
              name="customer"
              itemText="name"
              itemValue="id"
              :rules="[(v) => !!v || `${customerModule.customer} is required`]"
              :error-messages="formErrors.customer"
              v-model="customerAddressDetails.customer"
              @input="delete formErrors.customer"
            />
          </v-col>
        </v-row>
        <v-row class="ma-0">
          <v-col cols="12" lg="7">
            <v-card outlined class="pa-6 rounded-lg">
              <v-row>
                <v-col cols="12">
                  <span class="text-subtitle-1 font-weight-bold">
                    Location Information
                  </span>
                </v-col>
                <v-col cols="12" lg="6">
                  <InputField
                    id="location_id"
                    label="Location ID"
                    :required="true"
                    name="location_id"
                    :error-messages="formErrors.location_id"
                    v-model="customerAddressDetails.location_id"
                    @input="delete formErrors.location_id"
                  />
                </v-col>
                <v-col cols="12" lg="6">
                  <InputField
                    id="address_info"
                    type="text"
                    name="address_info"
                    label="Building / Apartment"
                    v-model="customerAddressDetails.address_info"
                  />
                </v-col>
                <v-col cols="12" lg="12">
                  <v-text-field
                    ref="customerAddress"
                    outlined
                    id="customerAddress"
                    name="address"
                    placeholder="Address*"
                    hide-details="auto"
                    dense
                    background-color="background_color"
                    :rules="[(v) => !!v || `Address is required`]"
                    :error-messages="formErrors.address"
                    v-model.trim="customerAddressDetails.address"
                    @input="
                      delete formErrors.address;
                      delete formErrors.coordinates;
                    "
                  ></v-text-field>
                </v-col>
                <v-col cols="12" lg="6">
                  <InputField
                    id="latitude"
                    type="text"
                    name="latitude"
                    label="Latitude"
                    :class="
                      editMode ? 'unassigned lighten-3' : 'background_color'
                    "
                    :required="true"
                    :disabled="editMode"
                    :error-messages="formErrors.coordinates"
                    v-model.number="customerAddressDetails.coordinates.latitude"
                    @input="delete formErrors.coordinates"
                  ></InputField>
                </v-col>
                <v-col cols="12" lg="6">
                  <InputField
                    id="longitude"
                    type="text"
                    name="longitude"
                    label="Longitude"
                    :class="
                      editMode ? 'unassigned lighten-3' : 'background_color'
                    "
                    :required="true"
                    :disabled="editMode"
                    v-model.number="
                      customerAddressDetails.coordinates.longitude
                    "
                    :error-messages="formErrors.coordinates"
                    @input="delete formErrors.coordinates"
                  ></InputField>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
          <v-col cols="12" lg="5">
            <v-card outlined class="pa-6 rounded-lg h-100">
              <v-row>
                <v-col cols="12">
                  <span class="text-subtitle-1 font-weight-bold">
                    Contact Information
                  </span>
                </v-col>
                <v-col cols="6" lg="6">
                  <InputField
                    id="contact_person"
                    label="Contact Person"
                    class="background-white"
                    name="contact_person"
                    :error-messages="formErrors.contact_person"
                    v-model="customerAddressDetails.contact_person"
                    @input="delete formErrors.contact_person"
                  ></InputField>
                </v-col>

                <v-col cols="6" lg="6">
                  <InputField
                    id="contact_number"
                    type="number"
                    label="Contact Number"
                    class="background-white"
                    name="contact_number"
                    :rules="[
                      (val) =>
                        !val ||
                        (val.length >= 9 && val.length <= 12) ||
                        'Contact number should be between 9 to 12 digits!',
                    ]"
                    :error-messages="formErrors.contact_number"
                    v-model="customerAddressDetails.contact_number"
                    @input="delete formErrors.contact_number"
                  ></InputField>
                </v-col>
                <v-col cols="12">
                  <InputField
                    id="contact_email"
                    type="email"
                    label="Contact Email"
                    class="background-white"
                    name="contact_email"
                    :rules="[
                      (v) => !v || emailValidation(v) || 'Invalid email id',
                    ]"
                    :error-messages="formErrors.contact_email"
                    v-model="customerAddressDetails.contact_email"
                    @input="delete formErrors.contact_email"
                  ></InputField>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
          <v-col cols="12" lg="8">
            <v-card outlined class="pa-6 rounded-lg h-100">
              <v-row>
                <v-col cols="12">
                  <span class="text-subtitle-1 font-weight-bold">
                    Customer Availability Time
                  </span>
                </v-col>
                <v-col cols="12" class="pa-0 ma-0">
                  <v-row>
                    <v-col cols="12" lg="4">
                      <InputField
                        id="from_time"
                        v-model="customerAddressDetails.from_time"
                        label="From Time"
                        append-icon=""
                        name="from_time"
                        type="time"
                        :error-messages="formErrors.from_time"
                        :required="true"
                        @input="delete formErrors.from_time"
                      ></InputField>
                    </v-col>
                    <v-col cols="12" lg="4">
                      <InputField
                        id="to_time"
                        v-model="customerAddressDetails.to_time"
                        label="To Time"
                        name="to_time"
                        append-icon=""
                        type="time"
                        :error-messages="formErrors.to_time"
                        :required="true"
                        @input="delete formErrors.to_time"
                      ></InputField>
                    </v-col>
                    <v-col cols="12" lg="4">
                      <InputField
                        id="processing_time"
                        type="number"
                        class="background-white"
                        label="Processing Time"
                        name="processing_time"
                        :required="true"
                        :error-messages="formErrors.processing_time"
                        v-model="customerAddressDetails.processing_time"
                        @input="delete formErrors.processing_time"
                      ></InputField>
                    </v-col>
                    <v-col cols="12">
                      <span class="text-subtitle-1 font-weight-bold">
                        Other Information
                      </span>
                    </v-col>

                    <v-col cols="12" lg="8">
                      <TextAreaField
                        id="remarks"
                        type="text"
                        name="remarks"
                        label="Remarks"
                        height="80"
                        v-model="customerAddressDetails.remarks"
                        outlined
                      >
                      </TextAreaField>
                    </v-col>
                    <v-col cols="12" lg="4">
                      <SelectField
                        id="tags"
                        v-model="customerAddressDetails.tags"
                        :label="`${tripPlanningModule.tagPlural}`"
                        :multiple="true"
                        name="tags"
                        :itemsList="customerAddressTagList"
                        itemValue="id"
                        itemText="name"
                        :error-messages="formErrors.tags"
                        @input="delete formErrors.tags"
                      ></SelectField>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
          <v-col cols="12" lg="4">
            <WeekDays
              title="Preference days"
              v-model="customerAddressDetails.days"
              :error-messages="formErrors.days"
              @input="delete formErrors.days"
              :required="true"
            />
          </v-col>
        </v-row>
      </v-form>
    </template>
    <template #actions>
      <SecondaryButton
        id="customerAddressReset"
        v-if="!editMode"
        @click="resetForm()"
        btnClass="mr-2 text-uppercase rounded-lg"
        name="Reset"
      >
      </SecondaryButton>
      <PrimaryButton
        id="customerAddressSubmit"
        :disabled="!isValid"
        @click="submitCustomerAddressForm"
        btnClass="text-uppercase rounded-lg"
        name="Submit"
      >
      </PrimaryButton>
    </template>
  </BaseDialogForm>
</template>

<script>
import { bus } from "@/main";
import { loadGoogleInstance } from "@/utils/functions";
import BaseDialogForm from "@/components/LayoutComponents/BaseDialogForm.vue";
import { emailValidation, locationIdValidation } from "@/utils/functions.js";
import {
  customerAddressModule,
  customerModule,
  tripPlanningModule,
} from "@/utils/moduleMapping";

export default {
  components: { BaseDialogForm },
  props: {
    value: Boolean,
    editMode: {
      required: true,
      type: Boolean,
    },
    customerAddressId: {
      required: true,
    },
    customerParentObj: {
      type: [Object, null],
      default: null,
    },
  },
  data() {
    return {
      locationIdValidation,
      tripPlanningModule,
      customerAddressModule,
      customerModule,
      emailValidation,
      loadGoogleInstance,
      vehicleTagsList: [],
      menu1: null,
      menu2: null,
      isValid: false,
      formErrors: {},
      customerAddressDetails: {
        coordinates: {},
        tags: [],
      },
      selectedProject: null,
      userDetails: this.$helper.getUserDetailsLocalStorage(),

      customerAddressTagList: [],

      customerList: [],
    };
  },
  watch: {
    value(val) {
      if (val) {
        this.setAutoComplete();
        this.getCustomerAddressTagList();
        if (this.editMode && this.customerAddressId) {
          this.getCustomerAddressDetails(this.customerAddressId);
        } else {
          if (!this.customerParentObj) {
            this.getCustomerList();
          }
        }
      } else {
        this.resetForm();
      }
    },
  },
  computed: {
    showCustomerAddressForm: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
  },
  methods: {
    resetForm() {
      this.$refs.customerAddressForm.reset();
      this.customerAddressDetails = {
        coordinates: {},
        tags: [],
      };
      this.formErrors = {};
    },
    getCustomerAddressDetails(id) {
      this.$api.customerAddress
        .getCustomerAddressObject(id)
        .then((res) => {
          let resp = { ...res.data };
          if (!res.data.coordinates) {
            resp.coordinates = {};
          }
          this.customerAddressDetails = resp;
        })
        .catch((err) => {
          console.error(err);
          bus.$emit("showToastMessage", {
            content: "Can't fetch data!",
            color: "error",
          });
        });
    },
    getCustomerAddressTagList(params = {}) {
      bus.$emit("showLoader", true);
      params = {
        ...params,
        limit: "all",
      };
      this.$api.tag
        .getTagList(params)
        .then((res) => {
          this.customerAddressTagList = res.data;
          bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.log(err);
          bus.$emit("showLoader", false);
        });
    },
    setAutoComplete() {
      const interval = setInterval(async () => {
        if (this.$refs.customerAddress && window.google && window.google.maps) {
          clearInterval(interval);
          let input = document.getElementById("customerAddress");

          this.autocomplete = new window.google.maps.places.Autocomplete(input);

          if (this.userDetails?.preferred_branch?.country_code) {
            this.autocomplete.setComponentRestrictions({
              country: [this.userDetails.preferred_branch.country_code],
            });
          } else {
            this.autocomplete.setComponentRestrictions({
              country: ["AE"],
            });
          }

          this.autocomplete.addListener("place_changed", () => {
            let place = this.autocomplete.getPlace();
            let lat = place.geometry.location.lat().toFixed(6);
            let lon = place.geometry.location.lng().toFixed(6);

            this.customerAddressDetails = {
              ...this.customerAddressDetails,
              address: place.name + ", " + place.formatted_address,
              coordinates: {
                latitude: parseFloat(lat),
                longitude: parseFloat(lon),
              },
            };
          });
        } else {
          loadGoogleInstance();
        }
      }, 100);
    },
    submitCustomerAddressForm() {
      if (this.customerParentObj && !this.editMode) {
        this.customerAddressDetails.customer =
          typeof this.customerParentObj == typeof {}
            ? this.customerParentObj.id
            : this.customerParentObj;
      }
      if (!this.$refs.customerAddressForm.validate()) {
        bus.$emit("showToastMessage", {
          message: "Please correct all the errors to submit the form!",
          color: "error",
        });
        return;
      }
      if (!this.editMode) {
        this.$api.customerAddress
          .createCustomerAddressObject(this.customerAddressDetails)
          .then(() => {
            bus.$emit("showToastMessage", {
              message: "Customer Address created!",
              color: "success",
            });
            this.showCustomerAddressForm = false;
            this.$emit("refreshList");
          })
          .catch((err) => {
            this.formErrors = err.data;
          });
      } else {
        let payload = {
          ...this.customerAddressDetails,
        };
        this.$root
          .$confirm(
            "Confirm!",
            `Do you want to update all <b>"Unassigned Order(s)"</b> created with customer address ${this.customerAddressDetails.location_id}?`,
            {
              color: "warning",
              hasActions: true,
              hasHtml: true,
            }
          )
          .then((userInput) => {
            console.log(userInput);
            if (userInput) {
              payload = {
                ...payload,
                update_corresponding_orders_address: true,
              };
            } else {
              payload = {
                ...payload,
                update_corresponding_orders_address: false,
              };
            }
            this.$api.customerAddress
              .updateCustomerAddressObject(
                this.customerAddressDetails.id,
                payload
              )
              .then(() => {
                bus.$emit("showToastMessage", {
                  message: "Customer Address updated!",
                  color: "success",
                });
                this.showCustomerAddressForm = false;
                this.$emit("refreshList");
              })
              .catch((err) => {
                this.formErrors = err.data;
              });
          });
      }
    },
    getCustomerList() {
      bus.$emit("showLoader", true);
      this.$api.customer
        .getCustomerList({ limit: "all" })
        .then((res) => {
          this.customerList = res.data;
          bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          bus.$emit("showLoader", false);
        });
    },
  },
  mounted() {},
};
</script>
